import { actions } from "./slice";
import { db } from "initializer";
import {
  createSubscribeCollection,
  createSubscribeDocument,
} from "../firestoreModuleUtils/operators";
import {
  getUserCharacterByName,
  getCharacterCountByName,
  getCharacterById,
  originalName,
  getSortedMyRoomCharacterIds,
} from "./selectors";
import {
  Character,
  CharacterRecord,
  CharacterRecord_V2,
  UpdateCharacter,
} from "./records";
import { appStateMutate } from "../app.state/operations";
import { DefaultRootState, DefaultThunk } from "stores";
import { getAppState } from "../app.state/selectors";
import { getUid } from "../app.user/selectors";
import i18next from "i18next";
import {
  DocumentData,
  FirestoreDataConverter,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { addUndo } from "../entities.room.histories/slice";
import { addMessage } from "../entities.room.messages/operations";
import { SystemMessage } from "../entities.room.histories/records/updateCharacter";
import { nanoid } from "@reduxjs/toolkit";
import {
  deleteAllCharacterIDB,
  getCharacterByKeyIDB,
  writeCharacterIDB,
} from "modules/characterIDB";
import { encodeUnicodeToBase64 } from "modules/base64";
import {
  calcOrderAppendingToTail,
  DropProps,
  reorderGroupEntities,
  undoRedoReorderGroupEntities,
} from "../firestoreModuleUtils/ordaring";

const characterConverter: FirestoreDataConverter<DocumentData> = {
  toFirestore(character: UpdateCharacter) {
    return character;
  },
  fromFirestore(snapshot, options): Character {
    const data = snapshot.data(options)!;
    return CharacterRecord(data);
  },
};

export const charactersRef = (roomId: string) =>
  collection(db, "rooms", roomId, "characters").withConverter(
    characterConverter
  );

export const subscribeRoomCharacters = createSubscribeCollection(
  actions,
  (roomId: string) => charactersRef(roomId)
);
export const subscribeRoomCharacter = createSubscribeDocument(
  actions,
  (props: { roomId: string; characterId: string }) =>
    doc(charactersRef(props.roomId), props.characterId)
);

export const addCharacter = (roomId: string, data: UpdateCharacter) => () => {
  return addDoc(charactersRef(roomId), {
    ...CharacterRecord_V2(data),
    updatedAt: Date.now(),
    createdAt: Date.now(),
  });
};

export const importRoomCharacters =
  (charactersData: { [characterId: string]: UpdateCharacter }): DefaultThunk =>
  (_, getState) => {
    const state = getState();
    const roomId = getAppState(state, "roomId");
    const uid = getUid(state);
    if (!roomId || !uid) return;
    const characterIds = Object.keys(charactersData);
    const batch = writeBatch(db);
    const ref = charactersRef(roomId);
    characterIds.forEach((characterId) => {
      batch.set(
        doc(ref, characterId),
        CharacterRecord_V2({
          ...charactersData[characterId],
          owner: uid,
        })
      );
    });
    return batch.commit();
  };

const parseClipboardTextIfCharacter = (text: string) => {
  try {
    const parsed = JSON.parse(text);
    return parsed.kind === "character" || parsed.kind === "copiedCharacter"
      ? (parsed as CharacterClipboard)
      : null;
  } catch (_) {
    return null;
  }
};

export const importRoomCharacterFromClipboardText =
  (text: string): DefaultThunk =>
  (dispatch) => {
    const clipboard = parseClipboardTextIfCharacter(text);
    if (clipboard != null) {
      dispatch(addRoomCharacterToCenter(clipboard));
    } else {
      alert(i18next.t("クリップボードからのデータ読み込みに失敗しました"));
    }
  };

export const addRoomCharacter =
  (data: UpdateCharacter): DefaultThunk =>
  (_, getState) => {
    const state = getState();
    const roomId = getAppState(state, "roomId");
    const uid = getUid(state);
    if (!roomId || !uid) return null;
    const characterIds = getSortedMyRoomCharacterIds(state);
    return addDoc(charactersRef(roomId), {
      ...CharacterRecord_V2(data),
      owner: uid,
      order: calcOrderAppendingToTail(
        characterIds,
        state.entities.roomCharacters.entities
      ),
      updatedAt: Date.now(),
      createdAt: Date.now(),
    });
  };

const addRoomCharacterToCenter =
  (clipboardData: CharacterClipboard): DefaultThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const roomId = getAppState(state, "roomId");
    const uid = getUid(state);

    if (!roomId || !uid) return null;

    const cellSize = state.app.state.roomScreenCellSize;

    let record: Character;

    if (clipboardData.kind === "copiedCharacter") {
      const dbData = await getCharacterByKeyIDB(clipboardData.data);
      if (!dbData || !dbData.copyData) {
        alert(i18next.t("クリップボードからのデータ読み込みに失敗しました"));
        return;
      }
      record = CharacterRecord(dbData.copyData);
    } else {
      record = CharacterRecord_V2(clipboardData.data);
    }

    record.active = true;
    record.x = (-record.width * cellSize) / 2;
    record.y = (-record.height * cellSize) / 2;
    const characterIds = getSortedMyRoomCharacterIds(state);

    const character = {
      ...record,
      owner: uid,
      order: calcOrderAppendingToTail(
        characterIds,
        state.entities.roomCharacters.entities
      ),
      updatedAt: Date.now(),
      createdAt: Date.now(),
    };
    const doc = await addDoc(charactersRef(roomId), character);
    dispatch(
      addUndo({
        kind: "update-character",
        id: doc.id,
        before: null,
        after: { ...character, _id: doc.id },
      })
    );
    return;
  };

export const addRoomCharacterWithEdit =
  (data: UpdateCharacter): DefaultThunk =>
  (dispatch, getState) => {
    const state = getState();
    const roomId = getAppState(state, "roomId");
    const uid = getUid(state);
    if (!roomId || !uid) return null;
    const characterIds = getSortedMyRoomCharacterIds(state);
    const character = {
      ...CharacterRecord_V2(data),
      owner: uid,
      order: calcOrderAppendingToTail(
        characterIds,
        state.entities.roomCharacters.entities
      ),
      updatedAt: Date.now(),
      createdAt: Date.now(),
    };
    return addDoc(charactersRef(roomId), character).then((docRef: any) => {
      dispatch(
        appStateMutate((state: any) => {
          state.openRoomCharacter = true;
          state.openRoomCharacterId = docRef.id;
        })
      );
      dispatch(
        addUndo({
          kind: "update-character",
          id: docRef.id,
          before: null,
          after: { ...character, _id: docRef.id },
        })
      );
    });
  };

export const updateCharacterOwn =
  (characterId: string, active?: boolean): DefaultThunk =>
  (dispatch, getState) => {
    const state = getState();
    const uid = getUid(getState());
    const roomId = getAppState(state, "roomId");
    const character = getCharacterById(state, characterId);
    if (!uid || !roomId || !character) return;

    const updateData: UpdateCharacter = {
      active,
      owner: uid,
    };

    if (uid !== character.owner) {
      // ownerが変わる場合は末尾に追加
      const characterIds = getSortedMyRoomCharacterIds(state);
      updateData.order = calcOrderAppendingToTail(
        characterIds,
        state.entities.roomCharacters.entities
      );
    }

    dispatch(updateCharacter(roomId, characterId, updateData));
    dispatch(addUndoUpdateCharacter(characterId, updateData));
  };

export const updateCharacter =
  (roomId: string, characterId: string, data: UpdateCharacter) => () => {
    // format
    if (data.width !== undefined) {
      const size = Math.min(Math.max(data.width, 1), 100);
      data.width = size;
      data.height = size;
    }
    if (data.x !== undefined && data.y !== undefined) {
      data.x = ~~data.x;
      data.y = ~~data.y;
    }
    return setDoc(
      doc(charactersRef(roomId), characterId),
      {
        ...data,
        updatedAt: Date.now(),
      },
      { merge: true }
    );
  };

export const updateRoomCharacter =
  (characterId: string, data: UpdateCharacter): DefaultThunk =>
  (_, getState) => {
    const state = getState();
    const roomId = state.app.state.roomId;
    if (!roomId) return null;
    // format
    if (data.width !== undefined) {
      const size = Math.min(Math.max(data.width, 1), 100);
      data.width = size;
      data.height = size;
    }
    if (data.x !== undefined && data.y !== undefined) {
      data.x = ~~data.x;
      data.y = ~~data.y;
    }
    return setDoc(
      doc(charactersRef(roomId), characterId),
      {
        ...data,
        updatedAt: Date.now(),
      },
      { merge: true }
    );
  };

export const updateCurrentRoomCharacter =
  (data: UpdateCharacter): DefaultThunk =>
  (_, getState) => {
    const state = getState();
    const roomId = state.app.state.roomId;
    const characterId = state.app.state.openRoomCharacterId;
    if (!roomId || !characterId) return null;
    // format
    if (data.width !== undefined) {
      const size = Math.min(Math.max(data.width, 1), 100);
      data.width = size;
      data.height = size;
    }
    if (data.x !== undefined && data.y !== undefined) {
      data.x = ~~data.x;
      data.y = ~~data.y;
    }

    return setDoc(
      doc(charactersRef(roomId), characterId),
      {
        ...data,
        updatedAt: Date.now(),
      },
      { merge: true }
    );
  };

export const updateRoomCharacterState =
  (
    roomId: string,
    uid: string,
    characterName: string,
    speaking: boolean
  ): DefaultThunk =>
  (_, getState) => {
    const character = getUserCharacterByName(getState(), {
      name: characterName,
      uid,
    });
    if (!character) return null;
    return setDoc(
      doc(charactersRef(roomId), character._id),
      {
        speaking,
        updatedAt: Date.now(),
      },
      { merge: true }
    );
  };

export const addUndoUpdateCharacter =
  (
    characterId: string,
    data: UpdateCharacter,
    systemMessage?: SystemMessage
  ): DefaultThunk =>
  (dispatch, getState) => {
    const roomId = getState().app.state.roomId;
    const character = getCharacterById(getState(), characterId);
    if (!roomId || !character) return null;
    dispatch(
      addUndo({
        kind: "update-character",
        id: characterId,
        before: character,
        after: { ...character, ...data },
        systemMessage: systemMessage,
      })
    );
  };

export const deleteCharacter = (roomId: string, characterId: string) => () => {
  return deleteDoc(doc(charactersRef(roomId), characterId));
};

export const deleteRoomCharacter =
  (characterId: string): DefaultThunk =>
  (_, getState) => {
    const state = getState();
    const roomId = state.app.state.roomId;
    if (!roomId) return null;
    return deleteDoc(doc(charactersRef(roomId), characterId));
  };

export const duplicateCharacter =
  (roomId: string, characterId: string) => () => {
    return getDoc(doc(charactersRef(roomId), characterId)).then(
      (docSnapshot) => {
        const data = docSnapshot.data();
        if (!data) return;
        addDoc(charactersRef(roomId), {
          ...CharacterRecord(data),
          updatedAt: Date.now(),
          createdAt: Date.now(),
        });
      }
    );
  };

export const duplicateRoomCharacter =
  (characterId: string): DefaultThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const roomId = state.app.state.roomId;
    const character = state.entities.roomCharacters.entities[characterId];
    if (!roomId || !character) return null;
    const clone = CharacterRecord(character);
    const duplicateNum = getCharacterCountByName(state, clone);
    clone.name = originalName(clone.name) + "(" + duplicateNum + ")";
    const doc = await addDoc(charactersRef(roomId), {
      ...clone,
      updatedAt: Date.now(),
      createdAt: Date.now(),
    });
    dispatch(
      addUndo({
        kind: "update-character",
        id: doc.id,
        before: null,
        after: { ...clone, _id: doc.id },
      })
    );
  };

export const updateRoomCharacterWithMessage =
  (character: UpdateCharacter, systemMessage?: SystemMessage): DefaultThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const roomId = state.app.state.roomId;
    if (!roomId || !character._id) return;
    const roomCharacter = state.entities.roomCharacters.entities[character._id];

    dispatch(updateRoomCharacter(character._id, { ...character }));
    if (systemMessage) {
      // undo/redo実行前に盤面上に対象の駒が存在しなかった場合はメッセージを表示せず処理を終了
      if (!roomCharacter) return;
      let beforeValue: string | number = 0;
      let afterValue: string | number = 0;
      const target = systemMessage.target ?? "value";
      if (systemMessage.type === "status" && character.status) {
        const stateIdx = character.status.findIndex(
          (state) => state.label === systemMessage.label
        );
        afterValue = character.status[stateIdx][target];
      } else if (systemMessage.type === "default") {
        afterValue = character[systemMessage.label];
      }
      const stateIdx = roomCharacter.status.findIndex(
        (state) => state.label === systemMessage.label
      );
      // 対象のステータスが削除されていた場合もメッセージを表示せず処理を終了
      if (stateIdx === -1) return;
      beforeValue =
        systemMessage.type === "status"
          ? roomCharacter.status[stateIdx][target]
          : roomCharacter[systemMessage.label];
      const fieldLabel =
        target === "max" ? `${systemMessage.label}最大値` : systemMessage.label;
      dispatch(
        addMessage(
          roomId,
          null,
          {
            text: `/system [ ${character.name} ] ${fieldLabel} : ${beforeValue} → ${afterValue}`,
            name: "system",
            channel: "main",
          },
          false
        )
      );
    }
  };

type CharacterClipboard = CharacterClipboardData | CharacterCopiedData;

type CharacterClipboardData = {
  kind: "character";
  data: Partial<CharacterData>;
};

type CharacterCopiedData = {
  kind: "copiedCharacter";
  data: string;
};

export type CharacterData = {
  name: string;
  memo: string;
  initiative: number;
  externalUrl: string;
  status: {
    label: string;
    value: number;
    max: number;
  }[];
  params: { label: string; value: string }[];
  iconUrl: string | null;
  faces: { iconUrl: string | null; label: string }[];
  angle: number;
  width: number;
  height: number;
  secret: boolean;
  invisible: boolean;
  hideStatus: boolean;
  color: string;
  commands: string;
};

export const COPY_CHARACTER_CUSTOM_TYPE = "text/html";

export const copyRoomCharacterToClipboard =
  (characterId: string): DefaultThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const character = state.entities.roomCharacters.entities[characterId];
    if (!character) return null;

    const characterData: CharacterData = {
      name: character.name,
      memo: character.memo,
      initiative: character.initiative,
      externalUrl: character.externalUrl,
      status: character.status,
      params: character.params,
      iconUrl: character.iconUrl,
      faces: character.faces,
      angle: character.angle,
      width: character.width,
      height: character.height,
      secret: character.secret,
      invisible: character.invisible,
      hideStatus: character.hideStatus,
      color: character.color,
      commands: character.commands,
    };

    // Safariではクリックイベントハンドラ内で navigator.clipboard.write を呼び出す必要がある。
    // 非同期処理のコンテキストではクリックイベントハンドラとはみなされないため、 ClipboardItem の引数を Promise にする。
    const buildHtmlBlob = async () => {
      // indexedDBに格納
      await deleteAllCharacterIDB(); // コピー時に以前のデータを削除
      const keyIDB = nanoid();
      await writeCharacterIDB({
        key: keyIDB,
        copyData: characterData,
      });

      const characterClipboardData: CharacterCopiedData = {
        kind: "copiedCharacter",
        data: keyIDB,
      };

      const base64 = encodeUnicodeToBase64(
        JSON.stringify(characterClipboardData)
      );
      const span = document.createElement("span");
      span.setAttribute("data-metadata", base64);

      return new Blob([span.outerHTML], {
        type: COPY_CHARACTER_CUSTOM_TYPE,
      });
    };

    const nameBlob = new Blob([character.name || "NONAME"], {
      type: "text/plain",
    });

    // Android Chromeでは、 text/html に対応する text/plain も設定する必要がある
    const clipboardItem = new ClipboardItem({
      "text/plain": nameBlob,
      [COPY_CHARACTER_CUSTOM_TYPE]: buildHtmlBlob(),
    });

    navigator.clipboard
      .write([clipboardItem])
      .then(() => {
        dispatch(
          appStateMutate(
            (state) =>
              (state.errorSnackbarMessage = i18next.t(
                "コマデータをクリップボードにコピーしました。"
              ))
          )
        );
      })
      .catch(() =>
        alert(
          i18next.t(
            "エラー：コマデータをクリップボードにコピーしようとしましたが、失敗しました。"
          )
        )
      );
  };

export const reorderCharacters = reorderGroupEntities({
  selectOrderdIds: getSortedMyRoomCharacterIds,
  selectEntities: (state: DefaultRootState) =>
    state.entities.roomCharacters.entities,
  actionGroupReorder: actions.groupReorder,
  collectionRef: charactersRef,
  type: "character",
});

export const undoRedoReorderCharacters = undoRedoReorderGroupEntities({
  selectOrderdIds: getSortedMyRoomCharacterIds,
  selectEntities: (state: DefaultRootState) =>
    state.entities.roomCharacters.entities,
  actionGroupReorder: actions.groupReorder,
  collectionRef: charactersRef,
});

export const reorderMyRoomCharacters =
  (order: DropProps): DefaultThunk =>
  async (dispatch, getState) => {
    const uid = getUid(getState());
    const roomId = getAppState(getState(), "roomId");
    if (!uid || !roomId) return;
    dispatch(reorderCharacters(uid, order, roomId));
  };
